import { useEffect, useMemo, type ReactNode } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { PACK_COLORS } from '@/design-system/tokens'
import { SIGNATURES, type Signature, type SignatureKind } from '@/lib/celebrationSignatures'

/**
 * Full-screen celebration overlay for BabyOS's emotional moments — birth, first-of-a-kind
 * milestones, and other genuine landmarks. Deliberately non-generic:
 *   - Palette matches the pack that owns the moment (pregnancy pink for birth, etc)
 *   - Confetti is soft (small dots, spring physics, gentle drift) — not casino sparkles
 *   - Auto-dismisses after ~4s unless caller passes onDismiss
 *
 * Use sparingly. If Celebration fires more than once a day in real usage, tone it down.
 */
interface CelebrationProps {
  open: boolean
  title: string
  subtitle?: string
  detail?: ReactNode
  palette?: 'pregnancy' | 'early_care' | 'health' | 'needs'
  /** Per-person signature that swaps confetti flavor (falling dots, hearts, stars, etc). */
  signature?: SignatureKind
  onDismiss?: () => void
  actions?: ReactNode
}

export function Celebration({
  open,
  title,
  subtitle,
  detail,
  palette = 'early_care',
  signature,
  onDismiss,
  actions,
}: CelebrationProps) {
  const color = PACK_COLORS[palette].light
  const colorDeep = PACK_COLORS[palette].deep
  const sig: Signature = SIGNATURES.find((s) => s.slug === signature) ?? SIGNATURES[0]
  const reduceMotion = useReducedMotion() ?? false

  useEffect(() => {
    if (!open || !onDismiss) return
    // Auto-dismiss faster in reduced-motion mode — no animation to appreciate,
    // holding the overlay open just steals focus for no reason.
    const t = setTimeout(onDismiss, reduceMotion ? 2000 : 4200)
    return () => clearTimeout(t)
  }, [open, onDismiss, reduceMotion])

  // Particles computed once per open so they don't jitter on re-render.
  const particles = useMemo(() => {
    if (!open || reduceMotion) return []
    const [minSize, maxSize] = sig.particle_size_range
    return Array.from({ length: sig.particle_count }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: sig.motion === 'burst' ? 50 + (Math.random() - 0.5) * 20 : sig.motion === 'bloom' ? 40 + Math.random() * 20 : -5,
      delay: sig.motion === 'burst' ? Math.random() * 0.15 : Math.random() * 0.6,
      duration: sig.motion === 'twinkle' ? 1.2 + Math.random() * 0.8 : sig.motion === 'burst' ? 0.8 + Math.random() * 0.4 : 1.6 + Math.random() * 1.2,
      size: minSize + Math.random() * (maxSize - minSize),
      hue: i % 3 === 0 ? color : i % 3 === 1 ? colorDeep : '#F4EFE7',
      rotate: (Math.random() - 0.5) * 240,
      angle: Math.random() * Math.PI * 2, // for burst radial
    }))
  }, [open, reduceMotion, sig, color, colorDeep])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-6"
          style={{
            background: `radial-gradient(circle at 50% 40%, ${color}22, ${color}05 60%, #0B0F0E 100%), #0B0F0E`,
          }}
          initial={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.3 }}
        >
          {/* Particle layer — flavor depends on signature */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {particles.map((c) => {
              const commonStyle: React.CSSProperties = {
                left: `${c.x}%`,
                top: `${c.y}%`,
                width: c.size,
                height: c.size,
                color: c.hue,
              }
              // Motion presets
              const fallInit = { y: 0, opacity: 0, rotate: 0 }
              const fallAnim = { y: '115vh', opacity: [0, 1, 1, 0], rotate: c.rotate }
              const driftInit = { y: 0, opacity: 0, scale: 0.6 }
              const driftAnim = { y: '-70vh', opacity: [0, 1, 1, 0], scale: 1 }
              const twinkleInit = { opacity: 0, scale: 0.4, rotate: 0 }
              const twinkleAnim = { opacity: [0, 1, 0.6, 1, 0], scale: [0.4, 1.2, 0.9, 1.2, 0.6], rotate: c.rotate }
              const burstInit = { opacity: 0, x: 0, y: 0, scale: 0.4 }
              const burstAnim = { opacity: [0, 1, 0], x: Math.cos(c.angle) * 220, y: Math.sin(c.angle) * 180, scale: [0.4, 1.1, 0.6] }
              const bloomInit = { opacity: 0, scale: 0.2 }
              const bloomAnim = { opacity: [0, 0.55, 0], scale: 2.4 }

              const [init, anim] =
                sig.motion === 'drift' ? [driftInit, driftAnim] :
                sig.motion === 'twinkle' ? [twinkleInit, twinkleAnim] :
                sig.motion === 'burst' ? [burstInit, burstAnim] :
                sig.motion === 'bloom' ? [bloomInit, bloomAnim] :
                [fallInit, fallAnim]

              const transition = { duration: c.duration, delay: c.delay, ease: [0.16, 0.68, 0.36, 1] as const }

              if (sig.particle === 'heart') {
                return (
                  <motion.svg key={c.id} className="absolute" viewBox="0 0 24 24" style={commonStyle} fill="currentColor" initial={init} animate={anim} transition={transition}>
                    <path d="M12 21s-7-4.5-9.5-9.2C.7 8.5 3 4 7 4c2 0 3.5 1 5 2.6C13.5 5 15 4 17 4c4 0 6.3 4.5 4.5 7.8C19 16.5 12 21 12 21z" />
                  </motion.svg>
                )
              }
              if (sig.particle === 'star') {
                return (
                  <motion.svg key={c.id} className="absolute" viewBox="0 0 24 24" style={commonStyle} fill="currentColor" initial={init} animate={anim} transition={transition}>
                    <polygon points="12,2 15,9 22,10 17,15 18,22 12,18 6,22 7,15 2,10 9,9" />
                  </motion.svg>
                )
              }
              if (sig.particle === 'diamond') {
                return (
                  <motion.div key={c.id} className="absolute" style={{ ...commonStyle, background: c.hue, transform: 'rotate(45deg)' }} initial={init} animate={anim} transition={transition} />
                )
              }
              if (sig.particle === 'ring') {
                return (
                  <motion.div key={c.id} className="absolute rounded-full" style={{ ...commonStyle, background: 'transparent', border: `2px solid ${c.hue}` }} initial={init} animate={anim} transition={transition} />
                )
              }
              if (sig.particle === 'streak') {
                return (
                  <motion.div key={c.id} className="absolute" style={{ ...commonStyle, width: c.size, height: c.size * 8, background: c.hue }} initial={init} animate={anim} transition={transition} />
                )
              }
              // default: circle
              return (
                <motion.div key={c.id} className="absolute rounded-full" style={{ ...commonStyle, background: c.hue }} initial={init} animate={anim} transition={transition} />
              )
            })}
          </div>

          {/* Central card */}
          <motion.div
            className="relative z-10 max-w-sm text-center"
            initial={reduceMotion ? { scale: 1, opacity: 1, y: 0 } : { scale: 0.86, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={reduceMotion ? { scale: 1, opacity: 1 } : { scale: 0.94, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : { type: 'spring', damping: 20, stiffness: 260 }}
          >
            <motion.div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ background: color, color: '#0B0F0E' }}
              initial={reduceMotion ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', damping: 12, stiffness: 200, delay: 0.15 }}
            >
              <SproutGlyph />
            </motion.div>
            <div className="font-serif italic text-display text-text-primary leading-tight">
              {title}
            </div>
            {subtitle && (
              <div className="mt-3 text-body text-text-secondary">{subtitle}</div>
            )}
            {detail && <div className="mt-6 text-caption text-text-tertiary">{detail}</div>}
            {actions && <div className="mt-8 flex flex-col gap-2">{actions}</div>}
          </motion.div>

          {/* Dismiss zone (tap anywhere outside actions) */}
          {onDismiss && !actions && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss celebration"
              className="absolute inset-0 z-0 cursor-default"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function SproutGlyph() {
  return (
    <svg width="42" height="42" viewBox="0 0 100 100" fill="none">
      <path
        d="M50 78 C 48 60, 48 45, 50 30"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M50 34 Q 68 24, 82 32 Q 64 42, 50 40 Z" fill="currentColor" />
      <path d="M50 44 Q 34 36, 22 42 Q 38 52, 50 48 Z" fill="currentColor" opacity="0.7" />
    </svg>
  )
}
